import { useEffect, useMemo, useRef, useState } from "react";
import {
  resolveRecordingMediaSource,
  resolveRecordingMediaUrl,
  resolvePlaybackSourceType,
} from "./recordingMedia";

export function useResolvedRecordingMedia(
  variant,
  measurement,
  diagnosticContext = {},
) {
  const mediaSource = useMemo(
    () => resolveRecordingMediaSource(variant, measurement),
    [measurement, variant],
  );
  const playbackSourceType = useMemo(
    () => resolvePlaybackSourceType(mediaSource),
    [mediaSource],
  );
  const mediaRef = useRef(null);
  const [resolvedMediaUrl, setResolvedMediaUrl] = useState(
    mediaSource.directUrl,
  );
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [mediaElementError, setMediaElementError] = useState(null);
  const [playbackRate, setPlaybackRate] = useState(1);

  useEffect(() => {
    let cancelled = false;

    setMediaError(null);
    setMediaElementError(null);
    setPlaybackRate(1);

    if (!mediaSource.kind) {
      setLoadingMedia(false);
      setResolvedMediaUrl(null);
      return undefined;
    }

    if (mediaSource.directUrl) {
      setLoadingMedia(false);
      setResolvedMediaUrl(mediaSource.directUrl);
      return undefined;
    }

    if (!mediaSource.storagePath) {
      setLoadingMedia(false);
      setResolvedMediaUrl(null);
      return undefined;
    }

    setLoadingMedia(true);
    setResolvedMediaUrl(null);

    resolveRecordingMediaUrl(mediaSource)
      .then((mediaUrl) => {
        if (cancelled) {
          return;
        }
        setResolvedMediaUrl(mediaUrl);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setMediaError(error);
      })
      .finally(() => {
        if (cancelled) {
          return;
        }

        setLoadingMedia(false);
      });

    return () => {
      cancelled = true;
    };
  }, [diagnosticContext, mediaSource, measurement, playbackSourceType, variant]);

  useEffect(() => {
    if (!mediaRef.current) {
      return;
    }

    mediaRef.current.playbackRate = playbackRate;
  }, [playbackRate, resolvedMediaUrl]);

  useEffect(() => {
    const mediaElement = mediaRef.current;
    if (!mediaElement || !resolvedMediaUrl) {
      return undefined;
    }

    const handleError = () => {
      setMediaElementError(new Error("Unable to load the recording media."));
    };

    mediaElement.addEventListener("error", handleError);

    try {
      mediaElement.load();
    } catch (error) {
      setMediaElementError(new Error("Unable to load the recording media."));
    }

    return () => {
      mediaElement.removeEventListener("error", handleError);
    };
  }, [
    diagnosticContext,
    measurement,
    mediaSource,
    playbackSourceType,
    resolvedMediaUrl,
    variant,
  ]);

  return {
    loadingMedia,
    mediaError: mediaError || mediaElementError,
    mediaRef,
    mediaSource,
    playbackSourceType,
    playbackRate,
    resolvedMediaUrl,
    setPlaybackRate,
  };
}
